import loadjs from 'loadjs'
import set from 'lodash.set'
import { serializeError } from 'serialize-error'
import { IVideojsPlayer, IVideojs } from '../@types/video.js'
import { IIMAError, IIMAErrorEvent } from '../@types/video.js/videojs-ima'
import iconSkip from '../assets/img/iconSkip.png'
import {
  CustomPlayerEvent,
  IMAEvent,
  ModalEvent,
  PlayerEvent,
  AppEvent
} from '../constants/events'
import {
  AD_LABEL,
  IMPRESSION_COUNTDOWN,
  SKIPPABLE,
  TIMEOUT_LOAD,
  TIMEOUT_AD,
  TIMEOUT_POSTROLL,
  TIMEOUT_PREROLL,
  MAX_REDIRECTS,
  TIMEOUT_VAST,
  VPaidMode
} from '../constants/ima'
import {
  ACTIVE_ID,
  AUTOPLAY_DISABLED,
  AUTOPLAY_DISABLED_EXPIRES
} from '../constants/storage'
import {
  DISABLED,
  HIDDEN,
  MOBILE,
  PIP_BODY,
  PIP,
  SKIP_AD_BTN,
  ENABLED
} from '../constants/styles'
import { GOOGLE_API_CDN, IMA_CDN } from '../constants/url'
import { getAdDataFromIMAEvent, getAdTag, getNextAdTag } from './ads'
import { Components } from './components'
import { Config, IPlaylistItem, ICurrentVidConfig } from './config'
import { Controls } from './controls'
import { logger } from './logger'
import { Tracker } from './tracker'
import { Viewport } from './viewport'
import { handleGDPR } from './web-wrapper'
import { defaultTrackMap } from '../constants/video'
import { IBidResponse } from '../@types/prebid.js'
import { bidForceStatus } from './bidforcing'
import { AdRules } from './ad-rules'
import { IAdRequestConfig } from '../@types/ads.js'

interface IState {
  pip: boolean
  programmaticPause: boolean
  videosPlayed: number
}

export class Player {
  private config: Config
  private components: Components
  private player: IVideojsPlayer
  private controls: Controls
  private videojs: IVideojs
  private state: IState = {
    pip: false,
    programmaticPause: false,
    videosPlayed: 0
  }
  private currentBid: IBidResponse | undefined
  private currentAdReqestConfig: IAdRequestConfig | undefined
  private adRules: AdRules

  constructor(config: Config, components: Components) {
    this.config = config
    this.components = components
    this.player = null as any
    this.controls = null as any
    this.videojs = null as any
    this.adRules = null as any

    this.resetCurrentVidToCTP = this.resetCurrentVidToCTP.bind(this)
    this.play = this.play.bind(this)
  }

  public async init(): Promise<void> {
    const { components, config } = this

    Tracker.record(AppEvent.initPlayer, config.apiConfig)

    if (config.mobile) {
      document.body.classList.add(MOBILE)
    }

    config.npa = (await handleGDPR()) ? 1 : 0

    try {
      await this.loadIMA()
    } catch (err) {
      logger.log('IMA SDK already loaded')
    }

    set(
      window,
      `$mediavine.video.bidController[${config.uuid}].setNextBidForce`,
      config.setNextBidForce.bind(config)
    )
    set(
      window,
      `$mediavine.video.bidController[${config.uuid}].setBidForceDefault`,
      config.setBidForceDefault.bind(config)
    )
    set(
      window,
      `$mediavine.video.bidController[${config.uuid}].logCurrentBidForceProperties`,
      config.logCurrentBidForceProperties.bind(config)
    )

    // load player when placement comes into view
    const initPlayer = () =>
      this.initPlayer().catch((err: Error) => {
        console.error(err)
        Tracker.record(AppEvent.initPlayerError, config.apiConfig, {
          message: err.message,
          name: err.name,
          stack: err.stack
        })
        throw new Error('Problem initializing Video.js')
      })

    if (
      Viewport.viewable(components.dom.rootContainer) ||
      config.videoStartTime
    ) {
      initPlayer()
    } else {
      // Start loading VJS + IMA when the viewport gets within 500px of the player
      // prettier-ignore
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
          initPlayer()
          observer.unobserve(entry.target)
        }
      }, {
        rootMargin: `0px 0px 500px 0px`,
        threshold: 0
      })
      observer.observe(components.dom.rootContainer)
    }
  }

  private async loadIMA(): Promise<void> {
    await loadjs([GOOGLE_API_CDN, IMA_CDN], 'mediavine-video__ima', {
      returnPromise: true
    })
  }

  private resetAdRules(duration: number): void {
    this.adRules = new AdRules(duration, { midroll: this.config.enableMidroll })
  }

  private async initPlayer(): Promise<void> {
    const { default: videojs } = await import('./vjs')
    const { config, components } = this
    const videoEl = components.dom.videoContainer.player
    const firstVideo = this.firstPlaylistItem

    let adTagUrl: string | false = false

    if (this.config.autoplay) {
      const currentVidConfig: ICurrentVidConfig = {
        permalink: config.permalink,
        slug: config.slug,
        videoNumber: config.apiConfig.video.videoNumber,
        videosPlayed: config.playlist.length ? 0 : null
      }
      const results = await getAdTag(this.config, currentVidConfig)
      adTagUrl = results.adTagUrl
      this.currentBid = results.highBid
      this.currentAdReqestConfig =  results.adRequestConfig
    }

    this.videojs = videojs as any
    const player = (this.player = this.videojs(videoEl, {
      aspectRatio: config.aspectRatio,
      autoplay: config.autoplay,
      controls: true,
      // @ts-ignore
      crossOrigin: videojs.browser.IS_IPHONE ? 'anonymous' : null,
      fluid: true,
      html5: {
        dash: {
          setBufferTimeAtTopQuality: 5,
          setBufferTimeAtTopQualityLongForm: 5,
          setBufferToKeep: 5,
          setSegmentOverlapToleranceTime: 0.5
        },
        vhs: {
          withCredentials: false
        },
        // webvtt not supported on iphone
        nativeTextTracks: videojs.browser.IS_IPHONE,
        nativeCaptions: videojs.browser.IS_IPHONE
      },
      muted: config.muted,
      nativeControlsForTouch: false,
      poster: firstVideo.poster,
      preload: config.autoplay ? 'none' : 'metadata',
      sourceOrder: true,
      sources: firstVideo.sources,
      techOrder: ['html5']
    }) as IVideojsPlayer)

    this.initIMA(adTagUrl)

    this.resetAdRules(this.config.apiConfig.video.duration)

    player.ready(() => {
      Tracker.record(AppEvent.initVJS, config.apiConfig)
      this.controls = new Controls(player)

      // Set default volume
      player.volume(config.volume)

      // playlists
      player.playlist(config.playlist, config.startingPosition)
      player.playlist.repeat(true)
      player.playlist.autoadvance()

      this.registerPlayerEventListeners()
      this.registerIntersectionObserver()
    })

    // silence the noise logs
    if (this.videojs.Html5DashJS) {
      this.videojs.Html5DashJS.hook('beforeinitialize', (_, mediaPlayer) => {
        mediaPlayer.getDebug().setLogToBrowserConsole(false)
      })
    }

    if (config.mobile) {
      Player.setupMobileTapClick(player)
    }

    if (!config.autoplay) {
      this.clickToPlay(firstVideo)
    }
  }

  private initIMA(adTagUrl) {
    const { config, components, player } = this
    const firstVideo = this.firstPlaylistItem
    const videoEl = components.dom.videoContainer.player
    const videojs = this.videojs as any

    player.ima({
      adLabel: AD_LABEL,
      adTagUrl,
      // adsResponse: vmap,
      adsManagerLoadedCallback: () => {
        Tracker.record(AppEvent.initIMA, config.apiConfig)
        this.registerImaEventListeners()

        // start mechanism for autoplay video
        // Note: this needs to go in the adsManagerLoadedCallback because
        // adsTagUrl has been set, but we can't start the video until the
        // ad manager has loaded
        if (config.autoplay) {
          // If the player is above the viewport, play the video
          // else, set programmaticPause = true, so we can start the video
          // once the player enters the viewport
          if (
            Viewport.above(components.dom.rootContainer) ||
            Viewport.in(components.dom.rootContainer)
          ) {
            this.controls.play()
          } else {
            this.state.programmaticPause = true
          }
        }
      },
      adsRenderingSettings: {
        loadVideoTimeout: TIMEOUT_LOAD
      },
      adsRequest: {
        contentDuration: firstVideo.duration,
        contentKeywords: firstVideo.keywords,
        contentTitle: firstVideo.title
      },
      contribAdsSettings: {
        debug: config.debug,
        postrollTimeout: TIMEOUT_POSTROLL,
        prerollTimeout: TIMEOUT_PREROLL,
        timeout: TIMEOUT_AD
      },
      disableCustomPlaybackForIOS10Plus: true,
      id: videoEl.id,
      numRedirects: MAX_REDIRECTS,
      showControlsForJSAds: true,
      showCountdown: true,
      vastLoadTimeout: TIMEOUT_VAST,
      vpaidMode: VPaidMode.ENABLED,
    })
  }

  private registerIntersectionObserver(): void {
    const { controls, components } = this

    // prettier-ignore
    const observer = new IntersectionObserver(([entry]) => {
      const rect = entry.boundingClientRect
      const ratio = entry.intersectionRatio
      const viewportHeight = window.innerHeight

      // pip mode only if video is above viewport
      if (rect.top < 0 && controls.playing && ratio <= 0.5) {
        this.popout()
      } else if (ratio >= 0.5) {
        this.popin()
      }

      // pause if active player is below viewport
      if (rect.bottom > viewportHeight && controls.playing && ratio <= 0.5) {
        // TODO: make sure a pause event occurs if the video is loading (not playing)
        controls.pause()
        this.state.programmaticPause = true
      } else if (this.state.programmaticPause && ratio >= 0.5) {
        controls.play()
        this.state.programmaticPause = false
      }
    }, {
      rootMargin: '0px',
      threshold: 0.5
    })

    observer.observe(components.dom.rootContainer)
  }

  private registerPlayerEventListeners(): void {
    const { config, player, components, controls } = this

    // debug logging
    if (config.debug) {
      for (const playerEvent in PlayerEvent) {
        player.on(playerEvent, () => {
          logger.log('VIDEOJS:', playerEvent)
        })
      }
    }

    // only let one video play at a time
    // NOTE: it would be nice if we could observe 'storage' events from
    // the same tab, but the event is only fired on *other* tabs
    window.addEventListener(CustomPlayerEvent.activePlayerChange, (e: any) => {
      if (config.uuid !== e.detail) {
        controls.pause()
        this.popin()
      }
    })

    // pause active player on blug, resume on focus
    player.on(window.document as any, 'visibilitychange', () => {
      if (document.hidden && controls.playing) {
        controls.pause()
        this.state.programmaticPause = true
      } else if (config.active && this.state.programmaticPause) {
        controls.play()
        this.state.programmaticPause = false
      }
    })

    // closing the pip player pauses ad or video
    components.dom.titleBar.closeBtn.addEventListener('click', () => {
      const { config } = this
      controls.pause()
      this.popin()
      if (config.disableAutoplayOnClose) {
        config.storage.set(
          AUTOPLAY_DISABLED,
          true,
          true,
          AUTOPLAY_DISABLED_EXPIRES
        )
      }
    })

    player.on(PlayerEvent.loadeddata, () => {
      Tracker.record(AppEvent.vjsLoadedData, config.apiConfig)
    })

    player.on(PlayerEvent.adstart, () => {
      // always show the player if we are going to show the ad reporter
      this.unhide()
      // show adreporter when ad is playing
      components.dom.adReporter._el.classList.remove(HIDDEN)
      components.dom.adReporter.adReporterLogo.style.direction = 'block'

      this.unhide()
    })

    if (config.autoplay) {
      if (config.videoStartTime) {
        this.unhide()
        setTimeout(() => this.play(), 1)
      } else {
        this.whenNoAd(this.resetCurrentVidToCTP)
      }
    }

    player.on(PlayerEvent.play, () => {
      if (player.seeking() || player.scrubbing()) {
        return
      }

      // set active player
      this.setActivePlayer()

      // remove spinner
      player.removeClass('vjs-ad-loading')
    })

    player.on(PlayerEvent.playing, () => {
      if (player.currentTime() === 0) {
        player.removeClass('vjs-looping')
      }

      // hide adreporter when ad is not playing
      components.dom.adReporter._el.classList.add(HIDDEN)

      // ensure close button is enabled when ad is not playing
      components.dom.titleBar.closeBtn.classList.add(ENABLED)

      // ensure video is not hidden
      this.unhide()

      // if the video is above viewport when the video starts playing, pop out
      if (Viewport.above(components.dom.placementContainer)) {
        this.popout()
      }
    })

    player.on(PlayerEvent.seeking, () => {
      if (player.currentTime() === 0) {
        player.addClass('vjs-looping')
      }
    })

    player.on(PlayerEvent.ended, () => {
      const { placementContainer } = this.components.dom
      const inPipMode = placementContainer.classList.contains(PIP)

      // choose the right "in-between" modal, or skip it in PiP mode
      if (inPipMode) {
        this.advanceToNextVideo(this.resetCurrentVidToCTP)
      } else if (player.playlist.nextIndex() === 0) {
        this.createEndModal()
      } else {
        this.createUpNextModal()
      }
    })

    player.on(PlayerEvent.adserror, (e: IIMAErrorEvent, data) => {
      const error = this.getAdError(e, data)
      if (!error) {
        // this is a simulated custom thrown error and does not need to be recorded
        return
      }

      const currentBid = this.currentBid
      const currentAdConfig = this.currentAdReqestConfig

      const errorData = {
        errorCode: error.getErrorCode(),
        innerError: serializeError(error.getInnerError()),
        message: error.getMessage(),
        type: error.getType(),
        vastErrorCode: error.getVastErrorCode(),
        campaignId: currentBid?.campaignId,
        creativeId: currentBid?.creativeId,
        bidder: currentBid?.s2sBidder
          ? `${currentBid?.s2sBidder}_s2s`
          : currentBid?.bidder,
        adBreakType: currentAdConfig?.adBreakType,
        startDelay: currentAdConfig?.startDelay,
        size: currentBid?.size,
        timeToRespond: currentBid?.timeToRespond,
        cpm: currentBid?.cpm,
        ttl: currentBid?.ttl,
        s2s: currentBid?.s2sBidder
      }
      this.player.removeClass('vjs-waiting')

      Tracker.record(AppEvent.vjsAdsError, config.apiConfig, errorData)

      config.onError(AppEvent.vjsAdsError, e)
    })

    player.on(PlayerEvent.ended, (e) => {
      Tracker.record(AppEvent.vjsEnded, config.apiConfig)
    })

    player.on(PlayerEvent.error, (e) => {
      Tracker.record(AppEvent.vjsError, config.apiConfig)

      config.onError(AppEvent.vjsError, e)
    })

    // Progress the current position cookie to the next playlist item if you've
    // viewed more than ten seconds of the video. This is only necessary in case
    // the page is reloaded, to prevent seeing the same video over and over
    let currentPositionViewed = false
    player.on(PlayerEvent.timeupdate, () => {
      const currentTime = player.currentTime()
      const nextPosition = player.playlist.nextIndex()

      if (!currentPositionViewed && currentTime > 10 && nextPosition !== -1) {
        currentPositionViewed = true
        config.playlistPosition = nextPosition
      } else if (currentTime <= 10) {
        currentPositionViewed = false
      }
      const currentAdConfig = this.adRules.processTimeAndGetCurrentAd(
        currentTime
      )
      if (currentAdConfig) {
        this.getBidsAndPlayOnWin(this.currentPlaylistItem, currentAdConfig)
      }
    })

    // update text tracks
    player.on(PlayerEvent.loadedmetadata, () => {
      this.setupTracks()
    })
    player.one(PlayerEvent.loadedmetadata, () => {
      if (config.videoStartTime) {
        logger.log(
          `Advancing player ${config.uuid} to ${config.videoStartTime}`
        )
        player.currentTime(config.videoStartTime)
      }
    })
  }

  private setupTracks() {
    const player = this.player
    // gets language and transforms from "en-US" => "en"
    const browserLanguage = navigator.language.split('-')[0]
    const tracks = this.currentPlaylistItem.textTracks

    const { defaultVtt } = this.config.apiConfig.site
    const siteDefaultTrack = defaultTrackMap[defaultVtt]

    if (tracks.length > 0 && player.textTracks().length === 0) {
      tracks.forEach((track) => {
        this.player.addRemoteTextTrack(
          {
            default: track.default,
            label: track.label,
            kind: track.kind,
            src: track.src,
            srclang: track.srclang
          },
          false
        )
      })
    } else if (tracks.length === 0 && siteDefaultTrack) {
      // If the site has a defaultVtt set, use that if this video
      // doesn't have any text tracks of its own
      this.player.addRemoteTextTrack(siteDefaultTrack, false)
    }

    const existingTracks: TextTrack[] = []
    const textTracks = player.textTracks()
    for (let i = 0; i < textTracks.length; i++) {
      existingTracks[i] = textTracks[i]
    }

    let englishTrack: any
    let languageMatchTrack: any
    let defaultTrack: any

    existingTracks.forEach((track) => {
      track.mode = 'disabled'
      if (browserLanguage === track.language) {
        languageMatchTrack = track
      }
      // @ts-ignore
      else if (track.default === true) {
        defaultTrack = track
      } else if (track.language === 'en') {
        englishTrack = track
      }
    })

    // If the the video is both muted and autoplay, Turn on captions in this order:
    // 1.) There is a caption track that matches the browser's language
    // 2.) The pub-specified default track
    // 3.) There is an English Track
    // 4.) The first track available
    if (this.config.autoplay && player.muted()) {
      const enableTrack =
        languageMatchTrack ||
        defaultTrack ||
        englishTrack ||
        player.textTracks()[0]
      if (enableTrack) {
        enableTrack.mode = 'showing'
      }
    }
  }

  private registerImaEventListeners(): void {
    const { components, config, player } = this

    // debug logging
    if (config.debug) {
      for (const imaEvent in IMAEvent) {
        player.ima.addEventListener(imaEvent as any, () => {
          logger.log('VIDEOJS-IMA:', imaEvent)
        })
      }
    }

    // set ads to have the same volume as the player
    player.ima.addEventListener(IMAEvent.loaded, (e) => {
      const adsManager = player.ima && player.ima.getAdsManager()
      const adData = getAdDataFromIMAEvent(e)

      if (adsManager) {
        if (player.muted()) {
          adsManager.setVolume(0)
        } else {
          adsManager.setVolume(player.volume())
        }
      }

      Tracker.record(AppEvent.imaLoaded, config.apiConfig, adData)
    })

    player.ima.addEventListener(IMAEvent.start, (e) => {
      Tracker.record(AppEvent.imaStart, config.apiConfig)
      const ad = e.getAd()

      // set active player
      this.setActivePlayer()

      // ensure video is not hidden
      this.unhide()

      // if the video is above viewport when the video starts playing, pop out
      if (Viewport.above(components.dom.placementContainer)) {
        this.popout()
      }

      // trigger 'ad started' callback
      config.onAdStarted()

      // trigger 'viewable' callback and show 'close' button on PIP player
      this.hideCloseBtnUntilImpression()

      // skip button
      if (!ad.isSkippable() && ad.getDuration() > SKIPPABLE) {
        this.addSkipButton()
      }
    })

    player.ima.addEventListener(IMAEvent.resume, () => {
      this.setActivePlayer()
    })

    player.ima.addEventListener(IMAEvent.complete, () => {
      this.removeSkipButton()
    })

    player.ima.addEventListener(IMAEvent.skip, () => {
      this.removeSkipButton()
    })
  }

  private updateStateForNextVid(): void {
    ++this.state.videosPlayed
  }

  private advanceToNextVideo(whenNoAdAction): void {
    const { components, player } = this

    player.one(PlayerEvent.contentchanged, () => {
      const item = this.currentPlaylistItem

      components.updateTitleBarPermalink(item.title, item.permalink)

      this.updateStateForNextVid()
      this.resetAdRules(item.duration)
      this.whenNoAd(whenNoAdAction)
      this.getBidsAndPlayOnWin(item)
    })

    player.addClass('vjs-ad-loading')
    player.playlist.next()
  }

  private createUpNextModal(): void {
    const { player, components, config } = this
    const ModalDialog = this.videojs.getComponent('ModalDialog')
    const modal = new ModalDialog(player, {
      content: components.dom.dialogContainer.upNext._el,
      uncloseable: true
    })
    const replay = () => {
      const currentVideo = this.currentPlaylistItem
      const initialVolume = player.volume()
      const initialMuted = player.muted()

      modal.close()
      player.reset()
      player.autoplay(true)
      player.src(currentVideo.sources)
      player.poster(currentVideo.poster)
      this.muted(initialMuted)
      this.setupTracks()
      player.volume(initialVolume)
      player.play()
    }
    let isReplaying = false

    player.addChild(modal)

    modal.addClass('vjs-up-next-dialog')
    modal.open()
    components.loadYoutubeBtn('upnext')

    let timeToNext = 5
    const item = this.nextPlaylistItem
    const { countdown } = components.dom.dialogContainer.upNext
    const timer = setInterval(() => {
      timeToNext = timeToNext - 1
      if (timeToNext > 0) {
        countdown.remaining.innerText = `${timeToNext}`
      } else {
        this.advanceToNextVideo(this.resetCurrentVidToCTP)
        modal.close()
      }
    }, 1000)

    // update thumbnail and title
    this.components.updateUpNextDialog(item.poster, item.title, (e) => {
      e.preventDefault()
      this.muted(false)
      this.advanceToNextVideo(this.play)
      modal.close()
    })

    // show "replay" instead
    countdown._el.onclick = (e) => {
      e.preventDefault()
      clearInterval(timer)
      countdown._el.classList.add(DISABLED)
      countdown._el.onclick = (e) => {
        e.preventDefault()
        isReplaying = true
        replay()
      }
      countdown.action.innerText = 'Replay'
    }

    modal.on(ModalEvent.modalclose, () => {
      clearInterval(timer)

      // reset defaults
      countdown._el.classList.remove(DISABLED)
      countdown.action.innerText = 'Cancel'
      countdown.remaining.innerText = '5'
    })

    player.one(PlayerEvent.playing, () => {
      modal.close()
    })
  }

  private createEndModal(): void {
    const { player, components } = this
    const ModalDialog = this.videojs.getComponent('ModalDialog')
    const modal = new ModalDialog(player, {
      content: components.dom.dialogContainer.end._el,
      uncloseable: true
    })

    player.addChild(modal)

    components.loadYoutubeBtn('end')
    components.dom.dialogContainer.end.replay.onclick = (e) => {
      e.preventDefault()
      this.advanceToNextVideo(this.play)
      modal.close()
    }

    modal.addClass('vjs-end-modal')
    modal.open()

    modal.on(ModalEvent.modalclose, () => {
      player.removeClass('vjs-ended')
    })
  }

  private get firstPlaylistItem(): IPlaylistItem {
    const { config } = this
    return config.playlist[config.startingPosition]
  }

  private get currentPlaylistItem(): IPlaylistItem {
    const { config, player } = this
    return config.playlist[player.playlist.currentItem()]
  }

  private get nextPlaylistItem(): IPlaylistItem {
    const { config, player } = this
    return config.playlist[player.playlist.nextIndex()]
  }

  private popout(): void {
    const { site } = this.config.apiConfig
    const container = this.components.dom.placementContainer

    Components.clsGuard(container, () => {
      container.classList.add(PIP)
      document.body.classList.add(PIP_BODY)

      // We want to force the 16:9 aspect ratio when videos popout on mobile/tablet.
      // Otherwise, tall videos could take up the whole screen.
      if (!this.config.desktop) {
        this.player.aspectRatio('16:9')
      }

      // add top margin
      if (this.config.mobile && site.mobilePopoutPlacement.includes('top')) {
        container.style.top = `${site.mobilePopoutTopMargin || 0}px`
      }
    })
  }

  private popin(): void {
    const { site } = this.config.apiConfig
    const container = this.components.dom.placementContainer

    Components.clsGuard(container, () => {
      container.classList.remove(PIP)
      document.body.classList.remove(PIP_BODY)

      if (!this.config.desktop) {
        this.player.aspectRatio(this.config.aspectRatio)
      }

      // remove top margin
      if (this.config.mobile && site.mobilePopoutPlacement.includes('top')) {
        container.style.top = '0'
      }
    })
  }

  private whenNoAd(fn: any, noAdCount = 0): void {
    const { player, config } = this
    let bidForceCompleted = false

    const retryHandler = (e: IIMAErrorEvent, data: any) => {
      const adError = this.getAdError(e, data)
      const errorCode =
        (adError && adError.getErrorCode && adError.getErrorCode()) ||
        (data.errorCode as number)

      const noAdReturned = errorCode === 1009
      // TODO: find a way to get 'next highest bid'
      player.addClass('vjs-ad-loading')
      if (noAdCount > 2 || noAdReturned) {
        player.removeClass('vjs-ad-loading')

        bidForceCompleted = true
        bidForceStatus(
          config,
          `error encountered; proceeding with no ad function; error code: ${errorCode}, error count: ${noAdCount}`
        )
        config.bidForceCompleted()

        fn()
      } else {
        bidForceStatus(
          config,
          `error encountered; retrying; error code: ${errorCode}, error count: ${noAdCount}`
        )
        bidForceCompleted = true
        config.bidForceCompleted()

        noAdCount += 1

        // Yikes! MegaHax: We need to hack the contrib-ads state here in order for it to
        // attempt to play ads immediately after and adsError event.
        this.hackContribState()

        return this.forceLoadingState()
          .then(() => this.whenNoAd(fn, noAdCount))
          .then(() => {
            // Try to use the next highest bid in the auctionHouse
            return this.getNextBidAndPlayOnWin().catch((message) => {
              // If there was no bid found in the house, rerun the auction.
              return this.getBidsAndPlayOnWin()
            })
          })
      }
    }

    player.one(PlayerEvent.adserror, retryHandler)

    player.one(PlayerEvent.playing, () => {
      bidForceStatus(config, `removing no ad handler at count ${noAdCount}`)
      if (!bidForceCompleted) {
        bidForceStatus(config, 'bidForceCompleted in no ad handler')
        config.bidForceCompleted()
      }
      player.off(PlayerEvent.adserror, retryHandler)
    })
  }

  /**
   * Sets the contrib-ads internal state so that we can recover from an adsError with another ad,
   * instead of falling immediately back to the content video.
   */
  private hackContribState() {
    const state = this.player.ads._state
    if (state.constructor.name === 'BeforePreroll') {
      state.shouldResumeToContent = false
    } else if (state.constructor.name === 'Preroll') {
      state.contentResuming = false
    }
  }

  // this is a private named method so that it can be referenced and "unfollowed"
  // in the event that the first ad successfully plays
  private resetCurrentVidToCTP(): void {
    const { config, player } = this
    const currentVideo = this.currentPlaylistItem

    // reset the player to its initial state
    player.pause()
    player.currentTime(0)
    player.autoplay(false)
    player.reset()
    player.src(currentVideo.sources)
    player.poster(currentVideo.poster)
    this.setupTracks()
    player.volume(config.volume)
    player.removeClass('vjs-has-started')

    const uvpEvent = new Event('mv-init-uvp');
    window.dispatchEvent(uvpEvent)

    this.clickToPlay(currentVideo)
  }

  // register one-time listener for click-to-play initialization of ima
  private clickToPlay(video = this.currentPlaylistItem) {
    const { config, player } = this
    const initEvent = config.mobile
      ? PlayerEvent.mobileclick
      : PlayerEvent.click

    this.unhide()
    this.muted(false)

    player.one(initEvent, () => {
      this.getBidsAndForcePlay(video)
    })
  }

  private play(): void {
    const { controls } = this
    controls.play()
  }

  private muted(value: boolean): void {
    const player = this.player

    this.updateImaMutedSignal(value)
    player.muted(value)
  }

  private updateImaMutedSignal(value: boolean): void {
    this.player.options_.muted = value
  }

  private getCurrentVidConfig(): ICurrentVidConfig {
    const item = this.currentPlaylistItem
    return {
      permalink: item.permalink,
      slug: item.slug,
      videoNumber: item.videoNumber,
      videosPlayed: this.state.videosPlayed
    }
  }

  // This function will run an auction, but won't play the video if there is no winner.
  private getBidsAndPlayOnWin(
    item = this.currentPlaylistItem,
    adRequestConfig?: IAdRequestConfig
  ): Promise<void> {
    const { player } = this

    const currentVidConfig = this.getCurrentVidConfig()
    return getAdTag(this.config, currentVidConfig, adRequestConfig)
      .then(({ adTagUrl, highBid, adRequestConfig }) => {
        this.currentBid = highBid
        this.currentAdReqestConfig = adRequestConfig
        if (!adTagUrl) {
          // No bid was found
          this.currentBid = undefined
          player.trigger(PlayerEvent.adserror, { errorCode: 1009 })
          return
        }

        player.ima.controller.settings.adsRequest = {
          contentDuration: item.duration,
          contentKeywords: item.keywords,
          contentTitle: item.title,
          nonLinearAdSlotWidth: 480,
          nonLinearAdSlotHeight: 200
        }
        player.ima.initializeAdDisplayContainer()
        bidForceStatus(
          this.config,
          'getBidsAndPlayOnWin requesting ads for',
          adTagUrl
        )
        player.ima.changeAdTag(adTagUrl)
        this.imaRequestAds()
      })
      .catch((err) => {
        console.warn('Failed to get ad tag', err)
      })
  }

  // This function get the next highest bid in the wrapper's auctionHouse,
  // but won't play the video if there is no bid.
  private getNextBidAndPlayOnWin(
    item = this.currentPlaylistItem,
    adRequestConfig?: IAdRequestConfig
  ): Promise<void> {
    const { player } = this

    const currentVidConfig = this.getCurrentVidConfig()
    return getNextAdTag(this.config, currentVidConfig, adRequestConfig)
      .then(({ adTagUrl, highBid, adRequestConfig }) => {
        this.currentBid = highBid
        this.currentAdReqestConfig = adRequestConfig
        if (!adTagUrl) {
          throw 'No adTag'
        }

        // console.log('getNextBidAndPlayOnWin', adTagUrl, highBid)

        player.ima.controller.settings.adsRequest = {
          contentDuration: item.duration,
          contentKeywords: item.keywords,
          contentTitle: item.title
        }
        player.ima.initializeAdDisplayContainer()
        bidForceStatus(
          this.config,
          'getNextBidAndPlayOnWin requesting ads for',
          adTagUrl
        )
        player.ima.changeAdTag(adTagUrl)
        this.imaRequestAds()
      })
      .catch((err) => {
        console.warn('Failed to get ad tag', err)
        throw 'No adTag'
      })
  }

  private imaRequestAds(): void {
    // right before making a request for ads, we need to update videojs-ima property
    // which controls the muted signal in the request.

    this.updateImaMutedSignal(this.player.muted())
    this.player.ima.requestAds()
  }

  private forceLoadingState(): Promise<void> {
    return new Promise((resolve, reject) => {
      const { controls, player } = this
      setTimeout(() => {
        controls.pause()
        player.addClass('vjs-ad-loading')
        resolve()
      }, 1)
    })
  }

  // This function will run an auction and play the video regardless of if there is
  // a winning bid.  Good to be used in conjunction with real clicks.
  private getBidsAndForcePlay(item = this.currentPlaylistItem): Promise<void> {
    return this.forceLoadingState()
      .then(() => this.whenNoAd(this.play))
      .then(() => this.getBidsAndPlayOnWin(item))
  }

  private addSkipButton(): void {
    const { player } = this
    const adManager = player.ima.getAdsManager()
    const skipBtn = document.createElement('div')
    let timeLeft = 5

    skipBtn.classList.add(SKIP_AD_BTN)
    skipBtn.innerText = `Skip in ${timeLeft}`
    this.components.dom.placementContainer.appendChild(skipBtn)

    const interval = setInterval(() => {
      if (!player.ima.controller.sdkImpl.adPlaying) {
        return
      }

      timeLeft = timeLeft - 1

      if (timeLeft > 0) {
        skipBtn.innerHTML = `Skip in ${timeLeft}`
      } else {
        skipBtn.classList.add(ENABLED)
        skipBtn.innerHTML = `
          Skip Ad
          <img
            alt="Skip Ad"
            src="${iconSkip}"
          />
        `
        skipBtn.addEventListener('click', (e) => {
          e.stopPropagation()
          adManager.stop()
          this.removeSkipButton()
        })
        clearInterval(interval)
      }
    }, 1000)
  }

  // trigger 'viewable' callback and show 'close' button on PIP player after
  // time has elapsed to allow
  private hideCloseBtnUntilImpression(): void {
    const { closeBtn } = this.components.dom.titleBar
    let timeLeft = IMPRESSION_COUNTDOWN

    closeBtn.classList.remove(ENABLED)

    // We use a setInterval instead of setTimeout here so that we can pause the
    // countdown if the user pauses the video, either manually or programmatically
    // (e.g.: changes tabs or virtual desktops)
    const interval = setInterval(() => {
      if (!this.player.ima.controller.sdkImpl.adPlaying) {
        return
      }

      timeLeft = timeLeft - 1

      if (timeLeft <= 0) {
        this.config.onViewable()
        closeBtn.classList.add(ENABLED)
        clearInterval(interval)
      }
    }, 1000)
  }

  private removeSkipButton(): void {
    const div = this.components.dom.placementContainer
    const skipBtn = div.getElementsByClassName(SKIP_AD_BTN)[0]

    if (skipBtn) {
      div.removeChild(skipBtn)
    }
  }

  private unhide(): void {
    this.components.dom.videoContainer._el.classList.remove(HIDDEN)
  }

  private setActivePlayer(): void {
    const { config } = this
    const event = new CustomEvent(CustomPlayerEvent.activePlayerChange, {
      detail: config.uuid
    })

    window.dispatchEvent(event)
    config.storage.set(ACTIVE_ID, config.uuid, true)
    logger.log('active player:', config.uuid)
  }

  private getAdError(e: IIMAErrorEvent, data?): IIMAError | undefined {
    return e?.data?.AdError || (data?.adError as IIMAError)
  }

  private static setupMobileTapClick(player: IVideojsPlayer): void {
    let tapTriggerValid = false

    // started tap/touch on player
    player.on('touchstart', (ev) => {
      tapTriggerValid = true

      // scroll anywhere will cancel
      window.addEventListener('touchmove', function _() {
        tapTriggerValid = false
        window.removeEventListener('touchmove', _)
      })

      // tap/touch ended
      player.on('touchend', function _() {
        // if no scrolling happened, tap with no scroll is valid and triggered
        if (tapTriggerValid) {
          player.trigger(PlayerEvent.mobileclick)
        }
        player.off('touchend', _)
      })
    })
  }
}
